import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useAuth } from '../auth/AuthProvider'
import { useMe, useUpdateMe, useUsage, useBilling } from '../api/hooks'
import { uploadAvatar } from '../api/client'
import { Input, Textarea, PageSpinner } from '../components/ui'
import { fmtBytes, fmtDate } from '../lib/format'

export function Settings() {
  const { t, locale } = useI18n()
  const { signOut } = useAuth()
  const { data: me, isLoading } = useMe()
  const { data: usage } = useUsage()
  const { data: billing } = useBilling()
  const update = useUpdateMe()

  type FormShape = { displayName: string; about: string; country: string; city: string; firstHorseMemory: string }
  const [f, setF] = useState<FormShape | null>(null)
  const [saved, setSaved] = useState(false)

  if (isLoading || !me) return <PageSpinner />
  const form: FormShape = f || {
    displayName: me.displayName, about: me.about || '', country: me.country || '', city: me.city || '', firstHorseMemory: me.firstHorseMemory || '',
  }
  const set = (k: keyof FormShape) => (v: string) => setF({ ...form, [k]: v })

  const save = async () => {
    await update.mutateAsync(form as never)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }
  const onAvatar = async (file: File) => {
    const key = await uploadAvatar(file)
    await update.mutateAsync({ avatarKey: key } as never)
  }

  return (
    <main className="mx-auto max-w-2xl px-5 py-8">
      <h1 className="text-2xl font-extrabold tracking-tight">{t('settings.title')}</h1>

      <div className="card mt-6 p-6">
        <h2 className="mb-4 font-bold">{t('settings.profile')}</h2>
        <div className="mb-5 flex items-center gap-4">
          <div className="grid h-16 w-16 place-items-center overflow-hidden rounded-full bg-brand-100 dark:bg-brand-800 text-2xl">
            {me.avatarUrl ? <img src={me.avatarUrl} alt="" className="h-full w-full object-cover" /> : '👤'}
          </div>
          <label className="btn-ghost cursor-pointer !py-2 text-sm">
            {t('settings.changePhoto')}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onAvatar(e.target.files[0])} />
          </label>
        </div>
        <div className="space-y-4">
          <Input label={t('onboarding.name')} value={form.displayName} onChange={set('displayName')} />
          <Textarea label={t('onboarding.about')} value={form.about} onChange={set('about')} />
          <div className="grid grid-cols-2 gap-4">
            <Input label={t('onboarding.country')} value={form.country} onChange={set('country')} />
            <Input label={t('onboarding.city')} value={form.city} onChange={set('city')} />
          </div>
          <Textarea label={t('onboarding.memory')} value={form.firstHorseMemory} onChange={set('firstHorseMemory')} rows={3} />
          <button onClick={save} disabled={update.isPending} className="btn-primary disabled:opacity-60">
            {saved ? `✓ ${t('settings.saved')}` : t('common.save')}
          </button>
        </div>
      </div>

      <div className="card mt-6 p-6">
        <h2 className="mb-4 font-bold">{t('settings.plan')}</h2>
        <div className="flex items-center justify-between text-sm">
          <span className="text-brand-600 dark:text-brand-300">{t('settings.currentPlan')}</span>
          <span className="font-semibold capitalize">{billing?.plan} {billing?.plan === 'trial' && `· ${t('billing.daysLeft', { days: billing.daysLeft })}`}</span>
        </div>
        {billing?.plan === 'trial' && (
          <div className="mt-1 text-xs text-brand-500 dark:text-brand-400">{t('settings.trialEnds', { date: fmtDate(billing.trialEndsAt, locale) })}</div>
        )}
        <Link to="/billing" className="btn-ghost mt-4 inline-block !py-2 text-sm">{t('settings.manageBilling')}</Link>
      </div>

      <div className="card mt-6 p-6">
        <h2 className="mb-4 font-bold">{t('settings.storage')}</h2>
        <div className="h-2 w-full overflow-hidden rounded-full bg-brand-100 dark:bg-brand-800">
          <div className="h-full rounded-full bg-brand-600" style={{ width: `${usage?.percent ?? 0}%` }} />
        </div>
        <div className="mt-1 text-xs text-brand-500 dark:text-brand-400">{usage ? `${usage.percent}% · ${fmtBytes(usage.usedBytes)} / 500 MB` : '—'}</div>
      </div>

      <button onClick={() => signOut()} className="mt-8 w-full rounded-full border border-red-200 dark:border-red-900 px-6 py-3 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
        {t('settings.signOut')}
      </button>
    </main>
  )
}
